import React, { useEffect, useMemo, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import LaunchIcon from "@mui/icons-material/Launch";
import { Stack } from "@mui/material";
import { isAxiosError } from "axios";
import { formatISO } from "date-fns";
import { useLocation } from "react-router-dom";

import { useCreateTask } from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useCreateTask";
import { TaskDetails } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

// Encapsulated task creation data
interface TaskCreationData {
  taskType: string;
  taskTypeId: number;
  client: string | null;
  taskTypeTemplate: string | undefined;
}

// Tagged union for location state
interface CreateTaskLocationState {
  type: "createTask";
  data: TaskCreationData;
}

interface SubtaskLocationState {
  type: "subtask";
  taskDetails: TaskDetails;
}

// Union type for all possible location states
type LocationState = CreateTaskLocationState | SubtaskLocationState | undefined;

import { ATTACHMENTS_LIMIT_COUNT } from "../../Tasks/common";
import TaskWorkflows from "../../TaskWorkflows";
import TaskActionButtons from "../components/TaskActionButtons";
import {
  FormEngine,
  FormEngineProvider,
  FormSubmissionProvider,
} from "../FormEngine";
import {
  useFormMethodsContext,
  useFormSubmissionContext,
  useTemplateContext,
} from "../FormEngine/context";
import { FormData } from "../FormEngine/core/types";
import { mapFormDataToTaskRequest } from "../FormEngine/utils/mappers";
import { useTasksNavigation } from "../hooks";
import TaskMessageModal from "../TaskMessageModal/TaskMessageModal";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useFilesManagement from "@src/multiCustodian/hooks/useFilesManagement";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { TASK_CREATION_PROCESS_DOC } from "@src/multiCustodian/pages/Advisor/common";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import { MenuOption } from "@src/sharedComponents/Menu/MenuOption";
import Text from "@src/sharedComponents/Typography/Text";
import Upload from "@src/sharedComponents/Upload/Upload";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import AttachmentsSection from "./AttachmentsSection";

import styles from "../../Tasks/CreateTask/CreateTask.module.css";

const mapParentTaskToInitialValues = (
  parentTask: TaskDetails
): Record<string, unknown> => {
  // Preprocess parent task to convert Date objects to strings before Zod validation
  const preprocessedParentTask = {
    ...parentTask,
    fields: {
      ...parentTask.fields,
      // Convert Date to string format that Zod expects
      dueDate:
        parentTask.fields?.dueDate instanceof Date
          ? formatISO(parentTask.fields.dueDate, { representation: "date" }) // Converts to "YYYY-MM-DD"
          : parentTask.fields?.dueDate, // Keep null/string as-is
    },
  };

  const validatedParentTask = TaskDetails.safeParse(preprocessedParentTask);

  if (!validatedParentTask.success) {
    console.warn(
      "[CreateTaskWorkflow] Invalid parent task structure:",
      validatedParentTask.error
    );
    return {
      taskStatus: { id: "NOT_STARTED" },
    };
  }

  const validParent = validatedParentTask.data;

  if (!validParent.fields || typeof validParent.fields !== "object") {
    console.warn(
      "[CreateTaskWorkflow] Parent task has no fields or invalid fields structure"
    );
    return {
      taskStatus: { id: "NOT_STARTED" },
    };
  }

  const initialValues = {
    ...validParent.fields,
    title: undefined, // Clear title so user can set new subtask title
    taskStatus: { id: "NOT_STARTED" }, // Always start subtasks as NOT_STARTED
  };

  return initialValues;
};

const mapCreateTaskDataToInitialValues = (
  taskCreationData: TaskCreationData
): Record<string, unknown> => {
  const initialValues: Record<string, unknown> = {};

  if (taskCreationData.client) {
    initialValues.client = taskCreationData.client;
  }

  if (taskCreationData.taskTypeId !== undefined) {
    initialValues.taskType = taskCreationData.taskTypeId.toString();
  } else if (taskCreationData.taskType) {
    initialValues.taskType = taskCreationData.taskType;
  }

  // Set default task status for new tasks
  initialValues.taskStatus = { id: "NOT_STARTED" };

  return initialValues;
};

interface CreateTaskWorkflowProps {
  isSubtaskCreation?: boolean;
}

interface CreateTaskWorkflowWithModalProps extends CreateTaskWorkflowProps {
  parentTaskId?: string;
}

const CreateTaskWorkflowWithModal: React.FC<
  CreateTaskWorkflowWithModalProps
> = ({ isSubtaskCreation, parentTaskId }) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [shouldCloseDrawer, setShouldCloseDrawer] = useState(false);

  const handleConfirmClose = (): void => {
    setIsConfirmModalOpen(false);
    setShouldCloseDrawer(true);
  };

  const handleCancelClose = (): void => {
    setIsConfirmModalOpen(false);
  };

  const handleShowConfirmModal = (): void => {
    setIsConfirmModalOpen(true);
  };

  const handleDrawerClosed = (): void => {
    setShouldCloseDrawer(false);
  };

  return (
    <CreateTaskWorkflowInner
      isSubtaskCreation={isSubtaskCreation}
      parentTaskId={parentTaskId}
      onShowConfirmModal={handleShowConfirmModal}
      shouldCloseDrawer={shouldCloseDrawer}
      onDrawerClosed={handleDrawerClosed}
      isConfirmModalOpen={isConfirmModalOpen}
      onConfirmModalClose={handleCancelClose}
      onConfirmModalConfirm={handleConfirmClose}
    />
  );
};

interface CreateTaskWorkflowInnerProps extends CreateTaskWorkflowProps {
  onShowConfirmModal: () => void;
  shouldCloseDrawer: boolean;
  onDrawerClosed: () => void;
  isConfirmModalOpen: boolean;
  onConfirmModalClose: () => void;
  onConfirmModalConfirm: () => void;
  parentTaskId?: string;
}

const CreateTaskWorkflowInner: React.FC<CreateTaskWorkflowInnerProps> = ({
  isSubtaskCreation,
  onShowConfirmModal,
  shouldCloseDrawer,
  onDrawerClosed,
  isConfirmModalOpen,
  onConfirmModalClose,
  onConfirmModalConfirm,
  parentTaskId,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [_isMutating, setIsMutating] = useState(false);

  const { files, setFiles, removeFile, removeAllFiles } = useFilesManagement({
    countLimit: ATTACHMENTS_LIMIT_COUNT,
  });
  const { handleNavigateToTasksList } = useTasksNavigation();
  const statusNotification = useStatusNotification();
  const { isDirty } = useFormSubmissionContext();
  const { currentTemplate } = useTemplateContext();
  const { formMethods } = useFormMethodsContext();
  const auth = useAdvisorRequestAuth();

  // Early return if no auth - this component shouldn't render without auth
  if (!auth) {
    throw new Error("Authentication required for CreateTaskWorkflow");
  }

  const createTaskMutation = useCreateTask(auth);

  // Clear files on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      removeAllFiles();
    };
  }, [removeAllFiles]);

  const handleCloseDrawer = (): void => {
    if (isDirty) {
      onShowConfirmModal();
    } else {
      setIsDrawerOpen(false);
    }
  };

  useEffect(() => {
    if (shouldCloseDrawer) {
      setIsDrawerOpen(false);
      onDrawerClosed();
    }
  }, [shouldCloseDrawer, onDrawerClosed]);

  const handleOpenDocument = (): void => {
    window.open(TASK_CREATION_PROCESS_DOC, "_blank", "noopener,noreferrer");
  };

  const handlePersistFiles = (files: FileList | null): void => {
    if (files && files.length) {
      setFiles(files);
    }
  };

  const handleCreateMenuOptions = (fileName: string): MenuOption[] => {
    return [
      {
        label: "Delete",
        className: styles.textWarning,
        onClick: () => removeFile(fileName),
      },
    ];
  };

  const handleFormSubmit = async (formData: FormData): Promise<void> => {
    if (!currentTemplate?.templateId) {
      statusNotification("Template not loaded. Please try again.", "Error");
      return;
    }

    try {
      setIsMutating(true);

      // Map form data to API format
      const requestData = mapFormDataToTaskRequest(
        formData,
        currentTemplate.templateId,
        parentTaskId
      );

      // Prepare files for upload
      const filesForUpload: Record<string, File[]> = {};
      if (files.length > 0) {
        filesForUpload.attachments = Array.from(files);
      }

      // Make the API call
      await createTaskMutation.mutateAsync({
        requestData,
        ...(files.length > 0 && { files: filesForUpload }),
      });

      statusNotification(
        `${isSubtaskCreation ? "Subtask" : "Task"} created.`,
        "Success"
      );
      setIsDrawerOpen(false);
    } catch (error) {
      // Check if the error contains field-specific validation errors
      if (isAxiosError(error) && error.response?.status === 400) {
        const errorData = error.response.data;

        // Handle the specific format shown in the bug report
        if (
          errorData?.validationErrors &&
          typeof errorData.validationErrors === "object"
        ) {
          const { setError } = formMethods;

          // Set field-specific errors
          Object.entries(errorData.validationErrors).forEach(
            ([fieldName, errors]) => {
              if (Array.isArray(errors) && errors.length > 0) {
                setError(fieldName, {
                  type: "server",
                  message: errors.join(". "),
                });
              }
            }
          );

          // Show general error message
          statusNotification(
            errorData.message ||
              `Failed to create ${
                isSubtaskCreation ? "subtask" : "task"
              }. Please check the form for validation errors.`,
            "Error"
          );
          return;
        }
      }

      // Fallback to generic error handling
      statusNotification(
        extractAxiosErrorMessage(
          error,
          `Failed to create ${isSubtaskCreation ? "subtask" : "task"}.`
        ),
        "Error"
      );
    } finally {
      setIsMutating(false);
    }
  };

  useEffect(() => {
    if (!isDrawerOpen) {
      handleNavigateToTasksList();
    }
  }, [isDrawerOpen, handleNavigateToTasksList]);

  return (
    <TaskWorkflows>
      <Drawer
        isDrawerOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        footer={
          <Stack
            sx={{ width: "100%" }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Upload
              multiple
              onChange={handlePersistFiles}
              renderChild={(onClick) => (
                <IconButton
                  iconClassName={styles.attachmentIcon}
                  onClick={onClick}
                  IconComponent={AttachFileIcon}
                />
              )}
            />

            <TaskActionButtons
              onCancel={handleCloseDrawer}
              // Pass down loading state if needed - the SaveButton should handle this via form submission context
            />
          </Stack>
        }
      >
        <div className={styles.header}>
          <Text variant="title2">
            Create {isSubtaskCreation ? "Subtask" : "Task"}
          </Text>
          <Button
            variant="outlined"
            buttonType="secondary"
            text="Task Templates"
            endIcon={<LaunchIcon />}
            onClick={handleOpenDocument}
          />
        </div>

        <FormEngine
          key="create-task-form"
          onSubmit={handleFormSubmit}
          externalResources={{
            taskMeta: {
              isSystemGenerated: false,
            },
            authConfig: auth,
          }}
        />

        <AttachmentsSection
          files={files}
          onCreateMenuOptions={handleCreateMenuOptions}
        />

        <TaskMessageModal
          title="Are you sure you want to leave without saving?"
          isOpen={isConfirmModalOpen}
          onClose={{
            label: "Stay",
            action: onConfirmModalClose,
          }}
          onConfirm={{
            label: "Leave",
            action: onConfirmModalConfirm,
          }}
        >
          <Text variant="body" color="textSubtle">
            Your changes will not be saved.
          </Text>
        </TaskMessageModal>
      </Drawer>
    </TaskWorkflows>
  );
};

const CreateTaskWorkflow: React.FC<CreateTaskWorkflowProps> = (props) => {
  const location = useLocation<LocationState>();

  // Get location state data
  const locationState = location.state;

  // Extract data based on the tagged union type
  const createTaskData =
    locationState?.type === "createTask" ? locationState : undefined;
  const subtaskLocationData =
    locationState?.type === "subtask" ? locationState : undefined;
  const parentTaskDetails = subtaskLocationData?.taskDetails;

  // Compute initial values based on the type of workflow
  const initialValues = useMemo(() => {
    if (props.isSubtaskCreation && parentTaskDetails) {
      return mapParentTaskToInitialValues(parentTaskDetails);
    }

    if (createTaskData?.data) {
      return mapCreateTaskDataToInitialValues(createTaskData.data);
    }

    return {};
  }, [props.isSubtaskCreation, parentTaskDetails, createTaskData]);

  // Create a stable key for the provider to ensure clean state on workflow changes
  const providerKey = useMemo(() => {
    if (props.isSubtaskCreation && parentTaskDetails) {
      return `subtask-${parentTaskDetails.id}`;
    }
    if (createTaskData?.data?.taskTypeId) {
      return `create-task-${createTaskData.data.taskTypeId}-${
        createTaskData.data.client || "no-client"
      }`;
    }
    if (createTaskData?.data?.taskType) {
      return `create-task-${createTaskData.data.taskType}-${
        createTaskData.data.client || "no-client"
      }`;
    }
    return "create-task-default";
  }, [
    props.isSubtaskCreation,
    parentTaskDetails,
    createTaskData?.data?.taskTypeId,
    createTaskData?.data?.taskType,
    createTaskData?.data?.client,
  ]);

  return (
    <FormSubmissionProvider>
      <FormEngineProvider key={providerKey} initialValues={initialValues}>
        <CreateTaskWorkflowWithModal
          {...props}
          parentTaskId={parentTaskDetails?.id}
        />
      </FormEngineProvider>
    </FormSubmissionProvider>
  );
};

export default CreateTaskWorkflow;
